import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/apiClient';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  CreditCardIcon,
  CheckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  StarIcon,
  UsersIcon,
  CircleStackIcon,
  BoltIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

interface BillingStatus {
  plan_id: string;
  plan_name: string;
  plan_status: string;
  can_access_admin: boolean;
  trial_ends_at?: string;
  is_trial_expired: boolean;
  usage_stats: {
    customers_count: number;
    sources_count: number;
    ai_queries_count: number;
    user_seats_count: number;
  };
  plan_limits: {
    max_customers: number;
    max_sources: number;
    ai_queries_per_month: number;
    user_seats: number;
  };
  plan_features: string[];
  price_monthly: number;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
}

interface PlanConfig {
  name: string;
  description: string;
  price_monthly: number;
  features: string[];
  limits: {
    max_customers: number;
    max_sources: number;
    ai_queries_per_month: number;
    user_seats: number;
  };
}

interface AvailablePlans {
  plans: Record<string, PlanConfig>;
}

const SUPPORT_EMAIL = 'beni_l@edytio.com';
const SUPPORT_PRICING_LABEL = 'Contact Support for pricing';
const supportMailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Edyt billing inquiry')}`;

const BillingPage: React.FC = () => {
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const queryClient = useQueryClient();

  // Fetch billing status
  const { data: billingStatus, isLoading: statusLoading } = useQuery<BillingStatus>({
    queryKey: ['billing-status'],
    queryFn: () => apiClient.customRequest('/api/billing/status'),
  });

  // Fetch available plans
  const { data: availablePlans, isLoading: plansLoading } = useQuery<AvailablePlans>({
    queryKey: ['available-plans'],
    queryFn: () => apiClient.customRequest('/api/billing/plans'),
  });

  // Cancel subscription mutation
  const cancelMutation = useMutation({
    mutationFn: () => 
      apiClient.customRequest('/api/billing/cancel-subscription', {
        method: 'POST'
      }) as Promise<{ message: string }>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing-status'] });
      setShowCancelConfirm(false);
    },
  });

  // Create billing portal session mutation
  const billingPortalMutation = useMutation({
    mutationFn: () => 
      apiClient.customRequest('/api/billing/billing-portal', {
        method: 'POST'
      }) as Promise<{ url: string }>,
    onSuccess: (data: { url: string }) => {
      window.location.href = data.url;
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800">Active</Badge>;
      case 'trialing':
        return <Badge className="bg-blue-100 text-blue-800">Trial</Badge>;
      case 'expired':
        return <Badge className="bg-red-100 text-red-800">Expired</Badge>;
      case 'past_due':
        return <Badge className="bg-yellow-100 text-yellow-800">Past Due</Badge>;
      case 'canceled':
        return <Badge className="bg-gray-100 text-gray-800">Canceled</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatPlanPrice = (priceMonthly: number) => {
    if (priceMonthly === 0) {
      return 'Free';
    }
    return SUPPORT_PRICING_LABEL;
  };

  const getUsagePercentage = (current: number, limit: number) => {
    return Math.min(100, (current / limit) * 100);
  };


  if (statusLoading || plansLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Billing & Subscription</h1>
        <p className="text-muted-foreground">Manage your subscription and billing information.</p>
      </div>

      {/* Current Plan Status */}
      {billingStatus && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Current Plan</span>
              {getStatusBadge(billingStatus.plan_status)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">{billingStatus.plan_name}</h3>
                <p className="text-2xl font-bold text-green-600 mb-4">
                  {formatPlanPrice(billingStatus.price_monthly)}
                </p>
                
                {billingStatus.trial_ends_at && billingStatus.plan_status === 'trialing' && (
                  <div className="flex items-center space-x-2 text-blue-600 mb-4">
                    <ClockIcon className="w-4 h-4" />
                    <span className="text-sm">Trial ends on {formatDate(billingStatus.trial_ends_at)}</span>
                  </div>
                )}

                {billingStatus.is_trial_expired && (
                  <div className="flex items-center space-x-2 text-red-600 mb-4">
                    <ExclamationTriangleIcon className="w-4 h-4" />
                    <span className="text-sm">Your trial has expired. Please upgrade to continue.</span>
                  </div>
                )}

                <div className="space-y-2">
                  <h4 className="font-medium">Features included:</h4>
                  <ul className="space-y-1">
                    {billingStatus.plan_features.map((feature, index) => (
                      <li key={index} className="flex items-center space-x-2 text-sm">
                        <CheckIcon className="w-3 h-3 text-green-500" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-4">Usage This Month</h4>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Customers</span>
                      <span>{billingStatus.usage_stats.customers_count} / {billingStatus.plan_limits.max_customers}</span>
                    </div>
                    <Progress 
                      value={getUsagePercentage(billingStatus.usage_stats.customers_count, billingStatus.plan_limits.max_customers)}
                      className="h-2"
                    />
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Data Sources</span>
                      <span>{billingStatus.usage_stats.sources_count} / {billingStatus.plan_limits.max_sources}</span>
                    </div>
                    <Progress 
                      value={getUsagePercentage(billingStatus.usage_stats.sources_count, billingStatus.plan_limits.max_sources)}
                      className="h-2"
                    />
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>AI Queries</span>
                      <span>{billingStatus.usage_stats.ai_queries_count} / {billingStatus.plan_limits.ai_queries_per_month}</span>
                    </div>
                    <Progress 
                      value={getUsagePercentage(billingStatus.usage_stats.ai_queries_count, billingStatus.plan_limits.ai_queries_per_month)}
                      className="h-2"
                    />
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Team Members</span>
                      <span>{billingStatus.usage_stats.user_seats_count} / {billingStatus.plan_limits.user_seats}</span>
                    </div>
                    <Progress 
                      value={getUsagePercentage(billingStatus.usage_stats.user_seats_count, billingStatus.plan_limits.user_seats)}
                      className="h-2"
                    />
                  </div>
                </div>

                {billingStatus.stripe_subscription_id && (
                  <div className="mt-6 space-y-2">
                    <Button
                      onClick={() => billingPortalMutation.mutate()}
                      disabled={billingPortalMutation.isPending}
                      variant="outline"
                      className="w-full"
                    >
                      <CreditCardIcon className="w-4 h-4 mr-2" />
                      Manage Billing
                    </Button>
                    <Button
                      onClick={() => setShowCancelConfirm(true)}
                      variant="outline"
                      className="w-full text-red-600 border-red-300 hover:bg-red-50"
                    >
                      Cancel Subscription
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Available Plans */}
      {availablePlans && (
        <div>
          <h2 className="text-2xl font-bold mb-4">Available Plans</h2>
          <div className="grid md:grid-cols-2 gap-6 items-start">
            {Object.entries(availablePlans.plans).map(([planId, plan]) => (
              <Card key={planId} className={`relative h-full flex flex-col ${billingStatus?.plan_id === planId ? 'ring-2 ring-blue-500' : ''}`}>
                {billingStatus?.plan_id === planId && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-blue-500 text-white">Current Plan</Badge>
                  </div>
                )}
                
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl">{plan.name}</CardTitle>
                    {planId === 'startup' && <StarIcon className="w-5 h-5 text-yellow-500" />}
                  </div>
                  <p className="text-gray-600">{plan.description}</p>
                  <div className="text-lg font-semibold text-muted-foreground">
                    {formatPlanPrice(plan.price_monthly)}
                  </div>
                </CardHeader>
                
                <CardContent className="flex-1 flex flex-col">
                  <div className="space-y-4 flex-1">
                    <div>
                      <h4 className="font-medium mb-2">Features:</h4>
                      <ul className="space-y-1">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="flex items-center space-x-2 text-sm">
                            <CheckIcon className="w-3 h-3 text-green-500" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-medium mb-2">Limits:</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center space-x-1">
                          <UsersIcon className="w-3 h-3" />
                          <span>{plan.limits.max_customers} customers</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <CircleStackIcon className="w-3 h-3" />
                          <span>{plan.limits.max_sources} sources</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <BoltIcon className="w-3 h-3" />
                          <span>{plan.limits.ai_queries_per_month} queries/mo</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <ShieldCheckIcon className="w-3 h-3" />
                          <span>{plan.limits.user_seats} team members</span>
                        </div>
                      </div>
                    </div>

                    {billingStatus?.plan_id !== planId && (
                      <Button
                        asChild
                        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                      >
                        <a href={supportMailto}>
                          Contact Support
                          <ArrowRightIcon className="w-4 h-4 ml-2" />
                        </a>
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="text-red-600">Cancel Subscription</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                Are you sure you want to cancel your subscription? Your subscription will remain active until the end of the current billing period.
              </p>
              <div className="flex space-x-3">
                <Button
                  onClick={() => setShowCancelConfirm(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Keep Subscription
                </Button>
                <Button
                  onClick={() => cancelMutation.mutate()}
                  disabled={cancelMutation.isPending}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {cancelMutation.isPending ? <LoadingSpinner size="sm" /> : 'Cancel Subscription'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BillingPage;
